import React, { useState, useEffect } from "react";
import ApiEndpointCard from "./ApiEndpointCard";
import { Copy, Download, Clock, FileJson, Filter } from "lucide-react";
import { apiClient } from "../../../services/apiService";

interface Props {
  openApiSpec: any;
  selectedRepo: string;
  selectedVersion: string;
}

const OpenApiDocViewer: React.FC<Props> = ({
  openApiSpec,
  selectedRepo,
  selectedVersion,
}: Props) => {
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedTooltip, setCopiedTooltip] = useState(false);

  const tags = openApiSpec.tags || [];
  const endpointsByTag: Record<string, any[]> = {};
  const untaggedEndpoints: any[] = [];

  if (openApiSpec.paths) {
    Object.entries(openApiSpec.paths).forEach(
      ([path, methods]: [string, any]) => {
        Object.entries(methods).forEach(
          ([method, operation]: [string, any]) => {
            const endpointTags = operation.tags || ["untagged"];

            endpointTags.forEach((tag: string) => {
              if (!endpointsByTag[tag]) endpointsByTag[tag] = [];
              endpointsByTag[tag].push({ path, method, ...operation });
            });

            if (endpointTags.includes("untagged")) {
              untaggedEndpoints.push({ path, method, ...operation });
            }
          }
        );
      }
    );
  }

  if (tags.length === 0 && Object.keys(endpointsByTag).length > 0) {
    Object.keys(endpointsByTag).forEach((tag) => {
      if (tag !== "untagged") {
        tags.push({ name: tag, description: "" });
      }
    });
  }

  if (
    untaggedEndpoints.length > 0 &&
    !tags.find((t: any) => t.name === "untagged")
  ) {
    tags.push({ name: "untagged", description: "Endpoints without tags" });
  }

  useEffect(() => {
    if (tags.length > 0 && !activeTag) setActiveTag(tags[0].name);
  }, [tags, activeTag]);

  const filterEndpoints = (endpoints: any[]) => {
    if (!searchQuery) return endpoints;
    return endpoints.filter(
      (endpoint) =>
        endpoint.path.toLowerCase().includes(searchQuery.toLowerCase()) ||
        endpoint.summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        endpoint.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const currentEndpoints = activeTag
    ? filterEndpoints(endpointsByTag[activeTag] || [])
    : [];

  const handleCopyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(openApiSpec, null, 2));
    setCopiedTooltip(true);
    setTimeout(() => setCopiedTooltip(false), 2000);
  };

  const handleDownloadJson = () => {
    const dataStr =
      "data:text/json;charset=utf-8," +
      encodeURIComponent(JSON.stringify(openApiSpec, null, 2));
    const downloadAnchorNode = document.createElement("a");
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `openapi.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const downloadJsonFromS3 = async (repo: string, version: string) => {
    const s3FileUrl = `/api-contract/file/${repo}/versions/${version}`;
    try {
      const { data } = await apiClient(s3FileUrl);
      const dataStr =
        "data:text/json;charset=utf-8," +
        encodeURIComponent(JSON.stringify(data, null, 2));
      const downloadAnchorNode = document.createElement("a");
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", `rawJson.json`);
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    } catch (error) {
      console.error("Error downloading JSON from S3:", error);
    }
  };

  return (
    <div className="pb-12">
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8 border border-gray-100">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">
              {openApiSpec.info?.title || "API Documentation"}
            </h1>
            <p className="text-gray-600">
              {openApiSpec.info?.description || "No description available"}
            </p>
          </div>
          <div className="flex space-x-2">
            <div className="relative">
              <button
                onClick={handleCopyJson}
                className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
                title="Copy OpenAPI JSON"
              >
                <Copy size={18} />
              </button>
              {copiedTooltip && (
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 px-2 py-1 text-xs bg-gray-800 text-white rounded">
                  Copied!
                </div>
              )}
            </div>
            <button
              onClick={handleDownloadJson}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
              title="Download OpenAPI JSON"
            >
              <Download size={18} />
            </button>
            <button
              onClick={() => downloadJsonFromS3(selectedRepo, selectedVersion)}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
              title="View Raw JSON"
            >
              <FileJson size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        <div className="w-full md:w-1/4 lg:w-1/5">
          <div className="bg-white rounded-lg shadow-sm p-4 sticky top-4 border border-gray-100">
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Filter endpoints..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <h3 className="font-medium text-gray-700 mb-2">Endpoints</h3>
            <nav className="space-y-1">
              {tags.map((tag: any) => (
                <button
                  key={tag.name}
                  onClick={() => setActiveTag(tag.name)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between ${
                    activeTag === tag.name
                      ? "bg-indigo-50 text-indigo-700 font-medium"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <span>{tag.name === "untagged" ? "Other" : tag.name}</span>
                  <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                    {(endpointsByTag[tag.name] || []).length}
                  </span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="flex-1">
          {tags.length > 0 && activeTag ? (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-1 capitalize">
                  {activeTag === "untagged" ? "Other Endpoints" : activeTag}
                </h2>
                <p className="text-gray-600 text-sm">
                  {tags.find((t: any) => t.name === activeTag)?.description ||
                    ""}
                </p>
              </div>

              {currentEndpoints.length > 0 ? (
                <div className="space-y-2">
                  {currentEndpoints.map((endpoint, index) => (
                    <ApiEndpointCard
                      key={`${endpoint.path}-${endpoint.method}-${index}`}
                      path={endpoint.path}
                      method={endpoint.method}
                      summary={endpoint.summary || ""}
                      description={endpoint.description || ""}
                      parameters={endpoint.parameters || []}
                      requestBody={endpoint.requestBody}
                      responses={endpoint.responses}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <p className="text-gray-600">
                    {searchQuery
                      ? "No endpoints match your search query"
                      : "No endpoints available in this section"}
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <p className="text-gray-600">
                No API endpoints found in this specification
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpenApiDocViewer;
